import { useState, useEffect } from "react";
import API from "@/lib/api";
import { formatCurrency, formatPercent } from "@/lib/formatters";
import DataTable from "@/components/shared/DataTable";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Plus, Upload, Pencil, Trash2 } from "lucide-react";
import AddEditDialog from "@/components/shared/AddEditDialog";
import UploadDialog from "@/components/shared/UploadDialog";
import DeleteConfirmDialog from "@/components/shared/DeleteConfirmDialog";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip as ReTooltip,
} from "recharts";

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#151B24] border border-[#2D3748] rounded-sm p-3 text-sm shadow-xl">
      <p className="font-semibold text-foreground mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="font-data text-xs" style={{ color: p.fill }}>{p.dataKey}: {typeof p.value === "number" && p.value > 1000 ? formatCurrency(p.value) : p.value}</p>
      ))}
    </div>
  );
};

export default function Managers() {
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = () => setRefreshKey((k) => k + 1);

  useEffect(() => {
    API.get("/managers")
      .then((res) => setManagers(res.data.managers || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [refreshKey]);

  const columns = [
    { key: "name", label: "Manager", sortable: false, render: (v) => <span className="font-medium text-foreground">{v}</span> },
    { key: "headquarters", label: "HQ" },
    { key: "strategy_focus", label: "Focus" },
    { key: "num_funds", label: "Funds", align: "right" },
    { key: "aum", label: "AUM", align: "right", render: (v) => formatCurrency(v) },
    { key: "avg_irr", label: "Avg IRR", align: "right", render: (v) => <span className="text-emerald-500">{v}%</span> },
    { key: "total_properties", label: "Properties", align: "right" },
    { key: "actions", label: "", sortable: false, render: (_, row) => (
      <div className="flex gap-1">
        <button className="p-1 rounded-sm hover:bg-secondary transition-colors" onClick={(e) => { e.stopPropagation(); setEditItem(row); }} data-testid={`edit-${row.id}`}><Pencil size={14} className="text-muted-foreground hover:text-foreground" /></button>
        <button className="p-1 rounded-sm hover:bg-destructive/20 transition-colors" onClick={(e) => { e.stopPropagation(); setDeleteItem(row); }} data-testid={`delete-${row.id}`}><Trash2 size={14} className="text-muted-foreground hover:text-red-500" /></button>
      </div>
    )},
  ];

  if (loading) return <div className="p-6"><Skeleton className="h-96" /></div>;

  return (
    <div className="p-6 space-y-6" data-testid="managers-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Fund Managers</h1>
          <p className="text-sm text-muted-foreground mt-1">Manager performance and allocation analysis</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => setShowAdd(true)} data-testid="add-manager-btn" className="gap-1"><Plus size={14} />Add</Button>
          <Button size="sm" variant="secondary" onClick={() => setShowUpload(true)} data-testid="upload-manager-btn" className="gap-1"><Upload size={14} />Upload</Button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4">
        <Card className="col-span-7 border border-border bg-card">
          <CardHeader className="border-b border-border p-3">
            <CardTitle className="text-sm font-semibold">Manager AUM Ranking</CardTitle>
          </CardHeader>
          <CardContent className="p-4" data-testid="manager-aum-chart">
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={managers} layout="vertical" margin={{ left: 100 }}>
                <CartesianGrid strokeDasharray="4 4" stroke="#2D3748" horizontal={false} />
                <XAxis type="number" tick={{ fill: "#9CA3AF", fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={(v) => formatCurrency(v)} />
                <YAxis type="category" dataKey="name" tick={{ fill: "#9CA3AF", fontSize: 11 }} axisLine={false} tickLine={false} width={95} />
                <ReTooltip content={<CustomTooltip />} />
                <Bar dataKey="aum" fill="#3B82F6" radius={[0, 2, 2, 0]} barSize={20} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="col-span-5 border border-border bg-card">
          <CardHeader className="border-b border-border p-3">
            <CardTitle className="text-sm font-semibold">IRR Performance</CardTitle>
          </CardHeader>
          <CardContent className="p-4" data-testid="manager-irr-chart">
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={managers} layout="vertical" margin={{ left: 100 }}>
                <CartesianGrid strokeDasharray="4 4" stroke="#2D3748" horizontal={false} />
                <XAxis type="number" tick={{ fill: "#9CA3AF", fontSize: 12 }} axisLine={false} tickLine={false} domain={[0, 20]} />
                <YAxis type="category" dataKey="name" tick={{ fill: "#9CA3AF", fontSize: 11 }} axisLine={false} tickLine={false} width={95} />
                <ReTooltip content={<CustomTooltip />} />
                <Bar dataKey="avg_irr" fill="#10B981" radius={[0, 2, 2, 0]} barSize={20} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <Card className="border border-border bg-card">
        <CardHeader className="border-b border-border p-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold">All Managers</CardTitle>
            <span className="text-xs text-muted-foreground font-data">{managers.length} managers</span>
          </div>
        </CardHeader>
        <CardContent className="p-0" data-testid="managers-table">
          <DataTable columns={columns} data={managers} />
        </CardContent>
      </Card>
      <AddEditDialog open={showAdd || !!editItem} item={editItem} entityType="managers" onClose={() => { setShowAdd(false); setEditItem(null); }} onSave={refresh} />
      <UploadDialog open={showUpload} entityType="managers" onClose={() => setShowUpload(false)} onSuccess={refresh} />
      <DeleteConfirmDialog open={!!deleteItem} item={deleteItem} entityType="managers" onClose={() => setDeleteItem(null)} onDelete={refresh} />
    </div>
  );
}
